import { Controller } from "@hotwired/stimulus"
import { bufferToBase64url, base64urlToBuffer, csrfHeaders } from "./webauthn_codec"

// Passkey login (docs/autenticacio.md): fetches the WebAuthn options,
// asks the browser for a discoverable credential and posts it back.
export default class extends Controller {
  static values = {
    beginUrl: String,
    finishUrl: String,
    unsupportedMessage: String,
    checkingText: String,
    failedMessage: String
  }
  static targets = ["button", "error"]

  async authenticate(event) {
    event.preventDefault()

    if (!window.PublicKeyCredential) {
      this.showError(this.unsupportedMessageValue)
      return
    }

    const button = this.buttonTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = this.checkingTextValue

    try {
      const optionsResponse = await fetch(this.beginUrlValue, { method: "POST", headers: csrfHeaders() })
      const options = await optionsResponse.json()

      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: base64urlToBuffer(options.challenge),
          timeout: options.timeout,
          rpId: options.rpId,
          userVerification: options.userVerification || "preferred",
          allowCredentials: (options.allowCredentials || []).map((cred) => ({
            id: base64urlToBuffer(cred.id),
            type: cred.type,
            transports: cred.transports
          }))
        }
      })

      const finishResponse = await fetch(this.finishUrlValue, {
        method: "POST",
        headers: csrfHeaders(),
        body: JSON.stringify({
          credential: {
            id: credential.id,
            type: credential.type,
            rawId: bufferToBase64url(credential.rawId),
            authenticatorAttachment: credential.authenticatorAttachment,
            response: {
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
              authenticatorData: bufferToBase64url(credential.response.authenticatorData),
              signature: bufferToBase64url(credential.response.signature),
              userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
            }
          }
        })
      })
      const result = await finishResponse.json()

      if (!finishResponse.ok || !result.success) throw new Error(result.error || this.failedMessageValue)
      window.location.href = result.redirect_to
    } catch (error) {
      // NotAllowedError is the user cancelling the browser dialog
      if (error.name !== "NotAllowedError") this.showError(error.message)
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }
}
